export interface RankableAthleteCollege {
    id: number;
    college: { id: number };
    isFavourite?: boolean;
    favouriteRank?: number | null;
}

export function sortAthleteColleges<T extends RankableAthleteCollege>(colleges: T[]): T[] {
    return [...colleges].sort((a, b) => {
        const aFavourite = Boolean(a.isFavourite);
        const bFavourite = Boolean(b.isFavourite);
        if (aFavourite !== bFavourite) {
            return aFavourite ? -1 : 1;
        }
        if (aFavourite && bFavourite) {
            const aRank = a.favouriteRank ?? Number.MAX_SAFE_INTEGER;
            const bRank = b.favouriteRank ?? Number.MAX_SAFE_INTEGER;
            if (aRank !== bRank) {
                return aRank - bRank;
            }
        }
        return colleges.indexOf(a) - colleges.indexOf(b);
    });
}

export function favouriteCollegeIdsInRankOrder<T extends RankableAthleteCollege>(colleges: T[]): number[] {
    return sortAthleteColleges(colleges)
        .filter((college) => college.isFavourite)
        .map((college) => college.college.id);
}

export function nextFavouriteRank<T extends RankableAthleteCollege>(colleges: T[]): number {
    const maxRank = colleges.reduce((highest, college) => {
        if (!college.isFavourite || college.favouriteRank == null) {
            return highest;
        }
        return Math.max(highest, college.favouriteRank);
    }, 0);
    return maxRank + 1;
}

export function applyFavouriteRanks<T extends RankableAthleteCollege>(
    colleges: T[],
    orderedFavouriteIds: number[],
): T[] {
    const rankById = new Map(orderedFavouriteIds.map((collegeId, index) => [collegeId, index + 1]));
    return colleges.map((college) => {
        const rank = rankById.get(college.college.id);
        if (rank == null) {
            return college;
        }
        return { ...college, isFavourite: true, favouriteRank: rank };
    });
}

export function compactRemainingFavourites<T extends RankableAthleteCollege>(colleges: T[]): T[] {
    const remainingIds = favouriteCollegeIdsInRankOrder(colleges);
    return colleges.map((college) => {
        if (!college.isFavourite) {
            return { ...college, favouriteRank: null };
        }
        const rank = remainingIds.indexOf(college.college.id) + 1;
        return { ...college, favouriteRank: rank > 0 ? rank : college.favouriteRank };
    });
}

export function markCollegeFavourite<T extends RankableAthleteCollege>(
    colleges: T[],
    collegeId: number,
    isFavourite: boolean,
    favouriteRank: number | null,
): T[] {
    const updated = colleges.map((college) =>
        college.college.id === collegeId
            ? { ...college, isFavourite, favouriteRank }
            : college,
    );
    return isFavourite ? updated : compactRemainingFavourites(updated);
}
